<script>
import * as m from '@app/paraglide/messages.js';
import {
    showModal
} from '@app/store/modalStore.svelte.js';
import {
    eventGroupState, isSelectedEventGroupLocked
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    voiceState
} from '@app/store/voiceStore.svelte.js';
import {
    speakerState
} from '@app/store/speakerStore.svelte.js';
import {
    editorState
} from '@app/store/editorStore.svelte.js';
import { environment } from '@app/store/envStore.svelte.js';
import { clearEventAudio } from '@app/lib/audio/assignEventAudio.js';
import {
    toast } from '@zerodevx/svelte-toast';
import {
    insertHistoryAction
} from "@app/store/historyStore.svelte.js";
import colorDict from "@cclib/dict/colors.js";
import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";
import { v4 as uuidv4 } from 'uuid';

// Function to find an existing speaker or create a new one with the voice name
function findOrCreateSpeaker(voiceName) {
    // First check if a speaker with this name already exists
    let existingSpeaker = speakerState.find(speaker => speaker.name.toLowerCase() === voiceName.toLowerCase());
    
    if (existingSpeaker) {
        return existingSpeaker;
    }
    
    // If no matching speaker is found, create a new one
    // Get a color from the speakerOrder array based on the current number of speakers
    const colorName = colorDict.speakerOrder[speakerState.length % colorDict.speakerOrder.length];
    const colorHex = colorDict.hexColorMapping[colorName];
    
    // Create new speaker
    const newSpeaker = {
        id: uuidv4(),
        name: voiceName,
        colour: colorHex
    };
    
    // Add to speaker state
    speakerState.push(newSpeaker);
    
    return newSpeaker;
}

let isLocked = $derived(isSelectedEventGroupLocked());

function assignSpeakerVoice(voice){
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    if (eventGroupState[projectState.selected].selected.length > 0 && eventGroupState[projectState.selected] && isAudioGroupType(eventGroupState[projectState.selected].type)){
        // Check the speaker assignment behavior setting
        const behavior = editorState.speakerAssignmentBehavior;
        
        eventGroupState[projectState.selected].selected.forEach(eventId =>{
            if (eventGroupState[projectState.selected].events[eventId]){
                const currentEvent = eventGroupState[projectState.selected].events[eventId];
                const voiceChanged = voiceUtils.voiceKey(voice) !== voiceUtils.voiceKey(currentEvent.voice);
                
                // Always update voice-related properties when voice changes
                if (voiceChanged){
                    clearEventAudio(currentEvent);
                    currentEvent.voice = voice;
                    currentEvent.speakingStyle = voice.provider === 'ElevenLabs' ? voiceUtils.ELEVENLABS_DEFAULT_MODEL : 'default';
                }
                
                // Handle speaker assignment based on behavior setting
                let shouldAssignSpeaker = false;
                
                if (behavior === 'always') {
                    // Always assign speaker, regardless of voice change
                    shouldAssignSpeaker = true;
                } else if (behavior === 'new') {
                    // Only assign speaker when voice changes (current behavior)
                    shouldAssignSpeaker = voiceChanged;
                } else if (behavior === 'never') {
                    // Never automatically assign speakers
                    shouldAssignSpeaker = false;
                }
                
                if (shouldAssignSpeaker) {
                    const matchingSpeaker = findOrCreateSpeaker(voice.name);
                    currentEvent.speakers = [matchingSpeaker];
                }
            }
        });

        /* Becomes default */
        eventGroupState[projectState.selected].ad.voice = voice;
        
        toast.push(m.voices_event_voice_updated_toast(), {classes: ['toast-success']});

        insertHistoryAction({
            name: "voice update", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
                {
                    store: "speakerState",
                    value: JSON.stringify(speakerState),
                }
            ]
        });
    }
}
</script>
<div id='PinnedVoiceWrapper' class="rounded">
    <div class="row mx-1 py-1">
        <div id="PinnedVoices" class="col-11 resize-vertical">
            <div class="row">
                {#if environment.online}
                    <div class="col-2 p-2">
                        <button
                            id="selfRecord"
                            aria-label={m.voices_manual_record_aria_label()}
                            type="button"
                            class="btn btn-light w-100"
                            disabled={isLocked}
                            onclick={() => assignSpeakerVoice(
                                {
                                    "provider": "CCC",
                                    "name": "Manual Record",
                                    "styles": [
                                        "default"
                                    ],
                                    "gender": "male",
                                    "language": "en-US"
                                }
                            )}>
                            <!-- svelte-ignore a11y_missing_content -->
                            <h1 style="font-size: 2vw;">
                                <i class="bi bi-mic"></i>
                            </h1>
                            <p class="text-muted small m-0 text-capitalize text-truncate" title={m.voices_manual_record_title()}>{m.voices_manual_record_label()}</p>
                        </button>
                    </div>
                {/if}
                {#each voiceState as pinnedVoice (voiceUtils.voiceKey(pinnedVoice))}
                <div class="col-2 p-2">
                    <button
                        id={"pinnedVoiceBtn-" + voiceUtils.voiceKey(pinnedVoice)}
                        aria-label={m.voices_select_voice_aria_label({voiceName: pinnedVoice.name})}
                        type="button"
                        class="btn btn-light w-100"
                        disabled={isLocked}
                        onclick={() => assignSpeakerVoice(pinnedVoice)}>
                        <h1 style="font-size: 2vw;">
                            {#if pinnedVoice.gender === "male"}
                            <i class="bi bi-person-standing"></i>
                            {:else}
                            <i class="bi bi-person-standing-dress"></i>
                            {/if}
                        </h1>
                        <p class="text-muted small m-0 text-capitalize text-truncate" title='{pinnedVoice.name}'>{pinnedVoice.name}</p>
                    </button>
                </div>      
                {:else}
	            <div class="col-2 p-2">
                    <button
                        id="openVoiceManagerBtn"
                        aria-label={m.voices_open_manager_aria_label()}
                        type="button"
                        class="btn btn-secondary w-100"
                        onclick={() => showModal("voiceManager")}>
                        <!-- svelte-ignore a11y_missing_content -->
                        <h1 style="font-size: 2vw;">
                            <i class="bi bi-plus-square"></i>
                        </h1>
                        <p class="text-muted small m-0 text-capitalize text-truncate" title=''>{m.voices_add_voices_label()}</p>
                    </button>
                </div>    
                {/each}

            </div>
        </div>
        <div class="col-1 p-2">
            <button
                id="voiceManagerBtn"
                aria-label={m.voices_open_manager_aria_label()}
                type="button"
                class="btn btn-light btn-sm h-100 float-end"
                onclick={() => showModal("voiceManager")}>
                <i class="bi bi-three-dots"></i>
            </button>
        </div>
    </div>
</div>

<style lang="css">
    #PinnedVoiceWrapper{
        background-color: rgba(255, 255, 255, 0.03);
    }
</style>